import { useEffect, useMemo, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/ui-helpers";
import { Upload, X, FileText, Download } from "lucide-react";
import { inputCls, Field } from "./Projects";
import { toast, Toaster } from "sonner";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { PaginationControls } from "@/components/PaginationControls";

import { useFormTab } from "@/hooks/useFormTab";

const DOC_TYPES = [
  "Work Order", "Invoice", "Challan", "GRN", "Agreement", "Safety Document",
  "Purchase Order", "Purchase Invoice", "Payment Proof", "Completion Certificate", "Other",
];

export default function Documents() {
  const [filterType, setFilterType] = useState("ALL");

  const params = useMemo(() => (filterType !== "ALL" ? { document_type: filterType } : {}), [filterType]);
  const { items: list, total, page, pageSize, loading, setPage, setPageSize, reload } = usePaginatedList("/documents", params);
  const load = reload;
  const openFormTab = useFormTab();

  const filtered = list;

  const openUpload = () => openFormTab({
    path: "/documents/new",
    label: "New Document",
    icon: Upload,
    parent: "/documents",
    onSaved: load,
    render: ({ close }) => <UploadModal onClose={close} onSaved={close} />,
  });

  async function handleDelete(id) {
    if (!window.confirm("Delete document?")) return;
    await api.delete(`/documents/${id}`);
    toast.success("Deleted"); load();
  }

  return (
    <div>
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Documents"
        title="Document Management"
        subtitle="Upload and manage all project documents — work orders, agreements, GRNs, completion certificates."
        actions={
          <button data-testid="upload-doc-btn" onClick={openUpload} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
            <Upload size={14} /> Upload Document
          </button>
        }
      />

      <div className="mb-4 flex gap-2 flex-wrap">
        <button onClick={() => setFilterType("ALL")} className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold border ${filterType === "ALL" ? "bg-[#1A1D21] text-white border-[#1A1D21]" : "bg-white border-slate-300 text-slate-700"}`}>All</button>
        {DOC_TYPES.map(t => (
          <button key={t} onClick={() => setFilterType(t)} className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold border ${filterType === t ? "bg-[#1A1D21] text-white border-[#1A1D21]" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"}`}>{t}</button>
        ))}
      </div>

      {filtered.length === 0 ? <EmptyState title="No documents uploaded" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(d => (
            <Card key={d.id} className="p-5 hover:border-[#F97316] transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 flex items-center justify-center">
                    <FileText size={18} className="text-[#F97316]" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{d.document_type}</div>
                    <div className="font-display font-bold text-slate-900 text-sm line-clamp-1">{d.title}</div>
                  </div>
                </div>
              </div>
              <div className="space-y-1 text-xs text-slate-600">
                {d.project_name && <div>📍 {d.project_name}</div>}
                {d.related_party_name && <div>🤝 {d.related_party_name} <span className="uppercase text-[9px] text-slate-400">({d.related_party_type})</span></div>}
                <div>📅 {formatDate(d.created_at)} · {d.uploaded_by}</div>
              </div>
              {d.remarks && <div className="text-xs text-slate-500 italic mt-2 line-clamp-2">{d.remarks}</div>}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                <a href={d.file_url} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-wider font-bold text-[#F97316] hover:underline flex items-center gap-1"><Download size={11} /> View / Download</a>
                <button onClick={() => handleDelete(d.id)} className="text-[10px] uppercase tracking-wider text-red-600 hover:underline">Delete</button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {list.length > 0 && (
        <div className="mt-4 border border-slate-200 bg-white">
          <PaginationControls page={page} pageSize={pageSize} total={total} loading={loading} onPage={setPage} onPageSize={setPageSize} />
        </div>
      )}
    </div>
  );
}

function UploadModal({ onClose, onSaved }) {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [form, setForm] = useState({
    document_type: "Work Order", title: "",
    project_id: "", related_party_type: "", related_party_id: "",
    remarks: "", file_url: "", file_name: "", file_size: 0,
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  useEffect(() => {
    Promise.all([api.get("/projects-picker"), api.get("/clients/picker"), api.get("/vendors/picker")])
      .then(([p, c, ct]) => { setProjects(p.data); setClients(c.data); setVendors(ct.data); });
  }, []);

  const parties = form.related_party_type === "client" ? clients :
                  form.related_party_type === "vendor" ? vendors : [];

  async function save(e) {
    e.preventDefault();
    if (!file) { toast.error("Select a file to upload"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const project = projects.find(p => p.id === form.project_id);
      const party = parties.find(p => p.id === form.related_party_id);
      const payload = {
        ...form,
        project_name: project?.project_name || "",
        related_party_name: party?.name || "",
        file_url: up.data.url,
        file_name: up.data.filename,
        file_size: up.data.size,
      };
      await api.post("/documents", payload);
      toast.success("Document uploaded");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  }

  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-[38px] bg-white z-10 shadow-sm">
          <h2 className="font-display text-xl font-bold">Upload Document</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={save} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-[1400px] mx-auto">
          <Field label="Document Type *">
            <select required value={form.document_type} onChange={e => set("document_type", e.target.value)} className={inputCls}>
              {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Title *"><input data-testid="doc-title" required value={form.title} onChange={e => set("title", e.target.value)} className={inputCls} placeholder="e.g. DMRC WO #45" /></Field>
          <Field label="Project">
            <select value={form.project_id} onChange={e => set("project_id", e.target.value)} className={inputCls}>
              <option value="">—</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.project_code} · {p.project_name}</option>)}
            </select>
          </Field>
          <Field label="Related Party Type">
            <select value={form.related_party_type} onChange={e => setForm({ ...form, related_party_type: e.target.value, related_party_id: "" })} className={inputCls}>
              <option value="">—</option>
              <option value="client">Client</option>
              <option value="vendor">Vendor</option>
              <option value="internal">Internal</option>
            </select>
          </Field>
          {parties.length > 0 && form.related_party_type !== "internal" && (
            <Field label="Related Party">
              <select value={form.related_party_id} onChange={e => set("related_party_id", e.target.value)} className={inputCls}>
                <option value="">—</option>
                {parties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="File *" full>
            <input data-testid="doc-file" type="file" required onChange={e => setFile(e.target.files?.[0])} className="block w-full text-sm" />
            {file && <div className="mt-2 text-xs text-slate-500">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
          </Field>
          <Field label="Remarks" full><textarea rows={2} value={form.remarks} onChange={e => set("remarks", e.target.value)} className={inputCls} /></Field>
          <div className="md:col-span-2 flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-slate-300 text-sm font-bold uppercase tracking-wider">Cancel</button>
            <button data-testid="save-doc-btn" disabled={uploading} type="submit" className="px-5 py-2.5 bg-[#F97316] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-60">
              {uploading ? "Uploading…" : "Upload"}
            </button>
          </div>
        </form>
    </div>
  );
}
